import type { BulletproofSequenceMap } from './bulletproofSequencer';
import { createSentenceAudioBlocks, createSelectionAudioBlocks, isLocalAudio, type SentenceAudioBlock } from './audioBlockMap';
import { canonicalJson } from './cachedPlayback';
import { languageOptions } from '../../supabase/functions/_shared/speech-languages';
import { MAX_GEMINI_TEXT_CHARACTERS, MAX_CACHED_BLOCK_WORDS, MAX_CACHED_BLOCK_SOURCES } from '../../supabase/functions/_shared/audio-limits';
import { blockSettings, type BlockSettings } from '../../supabase/functions/_shared/block-settings';
type GroupOptions = Partial<BlockSettings> & { mergeAcrossBlocks?: boolean; strategy?: 'sequential'|'cached' };
const limits = (s: Partial<BlockSettings>) => blockSettings({ block_max_characters:s.maxCharacters, block_max_words:s.maxWords, block_max_sources:s.maxSources });

export const CACHED_AUDIO_APPROACH = 'Cached larger blocks';
export const audioSourceBlockIds = (block: SentenceAudioBlock) => block.sourceBlockIds ?? [block.sourceBlockId];
const words = (text: string) => text.match(/\S+/gu)?.length ?? 0;
const fullOptions = (options: Record<string, unknown>, text: string): Record<string, unknown> => {
  const field = options.gemini_intonation ? 'gemini_intonation' : options.ted_intonation ? 'ted_intonation' : undefined;
  if (!field) return languageOptions(options);
  const spec = options[field] as { segments: Array<Record<string, unknown>> };
  return { ...languageOptions(options), [field]: { ...spec, segments: [{ ...spec.segments[0], text }] } };
};
function signature(block: SentenceAudioBlock): string {
  const spec = (block.synthesisOptions.gemini_intonation ?? block.synthesisOptions.ted_intonation) as { segments?: Array<Record<string, unknown>> } | undefined;
  if (!spec) return canonicalJson(languageOptions(block.synthesisOptions));
  return canonicalJson({ ...block.synthesisOptions, ...(block.synthesisOptions.gemini_intonation
    ? { gemini_intonation: { ...spec, segments: spec.segments?.map(({ text: _text, ...settings }) => settings) } }
    : { ted_intonation: { ...spec, segments: spec.segments?.map(({ text: _text, ...settings }) => settings) } }) });
}
function hasEvent(block: SentenceAudioBlock) {
  if(block.vocalCue)return true;
  const spec = (block.synthesisOptions.gemini_intonation ?? block.synthesisOptions.ted_intonation) as { segments?: Array<Record<string, unknown>> } | undefined;
  return spec?.segments?.some(s => s.cue || Number(s.pause_before_ms ?? 0) > 0) ?? false;
}
function bounded(text: string, settings: BlockSettings): string[] {
  const tokens = text.match(/\S+/gu) ?? [];
  const parts: string[] = [];
  let part = '';
  let count = 0;
  for (const token of tokens) {
    if (token.length > MAX_GEMINI_TEXT_CHARACTERS) throw new Error('A single spoken word exceeds the audio block limit.');
    if (part && (part.length + token.length + 1 > settings.maxCharacters || count >= settings.maxWords)) {
      parts.push(part); part = ''; count = 0;
    }
    part = part ? `${part} ${token}` : token; count++;
  }
  if (part) parts.push(part);
  return parts;
}
/** Isolate delivery boundaries first; no mixed-emotion sentence request. */
export function cachedAudioPieces(atoms: SentenceAudioBlock[], options: Partial<BlockSettings> = {}): SentenceAudioBlock[] {
  const settings = limits(options);
  return atoms.flatMap(atom => {
    if (isLocalAudio(atom)) return [atom];
    const field = atom.synthesisOptions.gemini_intonation ? 'gemini_intonation'
      : atom.synthesisOptions.ted_intonation ? 'ted_intonation' : undefined;
    const spec = field ? atom.synthesisOptions[field] as { segments: Array<Record<string, unknown>> } : undefined;
    const segments = spec?.segments ?? [{ text: atom.text }];
    let volumeCursor=0;
    const fragments = segments.flatMap((segment, segmentIndex) => {
      const text = String(segment.text).trim();
      let options: Record<string, unknown> = field ? { [field]: { ...spec, segments: [segment] } } : {};
      if (field === 'gemini_intonation' && !segment.style && !segment.cue) options = {};
      if (field === 'ted_intonation' && (segment.emotion === 'neutral' || Number(segment.emotion_strength) === 0) &&
        !Number(segment.pause_before_ms) && !Number(segment.pitch_semitones) && !Number(segment.volume_db)) options = {};
      options = { ...options, ...languageOptions(atom.synthesisOptions) };
      const pieces = bounded(text, settings);
      return pieces.map((piece, index) => {
        const found=atom.text.indexOf(piece,volumeCursor);if(found>=0)volumeCursor=found;
        const from=Math.min(1,volumeCursor/Math.max(1,atom.text.length));volumeCursor+=piece.length;
        const to=Math.min(1,volumeCursor/Math.max(1,atom.text.length));
        const audioVolume=atom.audioVolume?{...atom.audioVolume,from:atom.audioVolume.from+(atom.audioVolume.to-atom.audioVolume.from)*from,
          to:atom.audioVolume.from+(atom.audioVolume.to-atom.audioVolume.from)*to}:undefined;
        let settings = fullOptions(options, piece);
        if (index > 0 && field === 'gemini_intonation' && segment.cue) {
          const cfg = settings.gemini_intonation as { segments: Array<Record<string, unknown>> };
          const { cue: _cue, ...rest } = cfg.segments[0];
          settings = { ...languageOptions(settings), gemini_intonation: { ...cfg, segments: [rest] } };
        }
        const unchanged = segments.length === 1 && pieces.length === 1;
        return { ...atom, id: unchanged ? atom.id : `${atom.id}:delivery:${segmentIndex}:piece:${index}`,
          ...(audioVolume?{audioVolume}:{}),
          sourceText: unchanged ? atom.sourceText : piece, text: piece, synthesisOptions: settings,
          sourceBlockIds: [atom.sourceBlockId],
          cacheSources: [{ text: piece, synthesisOptions: settings }] };
      });
    });
    const result: SentenceAudioBlock[]=[];let prefix='';
    for(const fragment of fragments) {
      if(!/[\p{L}\p{N}]/u.test(fragment.text)) {
        const previous=result[result.length-1];
        if(previous) {
          previous.text+=fragment.text;previous.sourceText+=fragment.sourceText;
          previous.synthesisOptions=fullOptions(previous.synthesisOptions,previous.text);
          previous.cacheSources=[{text:previous.text,synthesisOptions:previous.synthesisOptions}];
        } else prefix+=fragment.text;
      } else {
        if(prefix) {
          fragment.text=prefix+fragment.text;fragment.sourceText=prefix+fragment.sourceText;
          fragment.synthesisOptions=fullOptions(fragment.synthesisOptions,fragment.text);
          fragment.cacheSources=[{text:fragment.text,synthesisOptions:fragment.synthesisOptions}];prefix='';
        }
        result.push(fragment);
      }
    }
    return result.length ? result : fragments;
  });
}
/** Group adjacent matching passages; keep scene/actor/pause boundaries. */
export function groupCachedAudioBlocks(pieces: SentenceAudioBlock[], options: GroupOptions = {}): SentenceAudioBlock[] {
  const { mergeAcrossBlocks = true, strategy='cached' } = options;
  const settings = limits(options);
  if(strategy==='sequential')return pieces.map((block,sequenceIndex)=>({...block,sequenceIndex}));
  const result: SentenceAudioBlock[] = [];
  for (const source of pieces) {
    const block = { ...source, sourceBlockIds: [...audioSourceBlockIds(source)], cacheSources: source.cacheSources?.map(s => ({ ...s })) };
    const previous = result[result.length - 1];
    const adjacent = previous && (audioSourceBlockIds(previous).includes(block.sourceBlockId)
      || mergeAcrossBlocks && block.originalOrder === (previous.lastSourceOrder ?? previous.originalOrder) + 1);
    const text = previous ? `${previous.text} ${block.text}` : block.text;
    const sources = [...(previous?.cacheSources ?? []), ...(block.cacheSources ?? [])];
    if (previous && adjacent && !isLocalAudio(previous) && !isLocalAudio(block) &&
      previous.type === block.type && !['scene-heading','transition'].includes(block.type) &&
      previous.voiceId === block.voiceId && previous.character === block.character &&
      signature(previous) === signature(block) && !hasEvent(previous) && !hasEvent(block) &&
      previous.audioSpeed===block.audioSpeed&&
      JSON.stringify(previous.audioVolume&&[previous.audioVolume.group,previous.audioVolume.gainDb,previous.audioVolume.motion])===JSON.stringify(block.audioVolume&&[block.audioVolume.group,block.audioVolume.gainDb,block.audioVolume.motion])&&
      text.length <= settings.maxCharacters && words(text) <= settings.maxWords && sources.length <= settings.maxSources) {
      previous.id = previous.id.endsWith(':cached') ? previous.id : `${previous.id}:cached`;
      previous.text = text; previous.sourceText = `${previous.sourceText} ${block.sourceText}`;
      previous.synthesisOptions = fullOptions(previous.synthesisOptions, text);
      if(previous.audioVolume&&block.audioVolume)previous.audioVolume={...previous.audioVolume,to:block.audioVolume.to};
      previous.sourceBlockIds = [...new Set([...audioSourceBlockIds(previous), ...audioSourceBlockIds(block)])];
      previous.cacheSources = sources;
      // Last source position is used only for adjacency, not original playback order.
      previous.lastSourceOrder = block.lastSourceOrder ?? block.originalOrder;
    } else result.push(block);
  }
  return result.map((block, sequenceIndex) => ({ ...block, sequenceIndex }));
}
export function createCachedAudioBlocks(sequence: BulletproofSequenceMap, options: GroupOptions = {}) {
  return groupCachedAudioBlocks(cachedAudioPieces(createSentenceAudioBlocks(sequence), options), options);
}
export function createCachedSelectionAudioBlocks(sequence: BulletproofSequenceMap, id: string, start: number, end: number) {
  return groupCachedAudioBlocks(cachedAudioPieces(createSelectionAudioBlocks(sequence, id, start, end)), { mergeAcrossBlocks: false });
}
